import React from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Table } from 'reactstrap';
import { Address, formatAddress } from '../../models/Address.interface';
import { CostBreakdownItem } from '../DashboardProject/BidShared';

interface PaymentScheduleItem {
  milestoneSequence?: number;
  condition: string;
  percentage: number;
  isLocked: boolean;
}

interface BidPreviewModalProps {
  isOpen: boolean;
  toggle: () => void;
  onSubmit: () => void;
  contractorName: string;
  contractorLicense: string;
  contractorAddress: Address;
  contractorPhone: string;
  contractorEmail: string;
  bidType: string;
  fixedPriceType: string;
  bidAmountContractor: string;
  bidAmount: string;
  startDate: string;
  endDate: string;
  approach: string;
  warrantyPeriod: string;
  requireInspection: boolean;
  costBreakdown: CostBreakdownItem[];
  paymentSchedule: PaymentScheduleItem[];
  files: File[];
}

const BidPreviewModal: React.FC<BidPreviewModalProps> = ({
  isOpen,
  toggle,
  onSubmit,
  contractorName,
  contractorLicense,
  contractorAddress,
  contractorPhone,
  contractorEmail,
  bidType,
  fixedPriceType,
  bidAmountContractor,
  bidAmount,
  startDate,
  endDate,
  approach,
  warrantyPeriod,
  requireInspection,
  costBreakdown,
  paymentSchedule,
  files,
}) => {
  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Preview Your Bid</ModalHeader>
      <ModalBody>
        <h5>Contractor Information</h5>
        <p><strong>Contractor Name:</strong> {contractorName}</p>
        <p><strong>Contractor License:</strong> {contractorLicense}</p>
        <p><strong>Address:</strong> {formatAddress(contractorAddress)}</p>
        <p><strong>Phone:</strong> {contractorPhone}</p>
        <p><strong>Email:</strong> {contractorEmail}</p>

        <h5>Bid Details</h5>
        <p><strong>Bid Type:</strong> {bidType === 'fixedPrice' ? 'Fixed Price' : 'Hourly'}</p>
        {bidType === 'fixedPrice' && (
          <p><strong>Fixed Price Type:</strong> {fixedPriceType === 'laborAndMaterials' ? 'Labor & Materials' : 'Labor Only'}</p>
        )}
        <p><strong>Contractor Price:</strong> ${bidAmountContractor} {bidType === 'hourly' && 'per hour'}</p>
        <p><strong>Homeowner Price:</strong> ${bidAmount} {bidType === 'hourly' && 'per hour'}</p>
        <p><strong>Estimated Start Date:</strong> {startDate}</p>
        <p><strong>Estimated Completion Date:</strong> {endDate}</p>
        <p><strong>Approach Description:</strong> {approach}</p>
        <p><strong>Warranty Period:</strong> {warrantyPeriod} days</p>
        <p><strong>Require Inspection:</strong> {requireInspection ? 'Yes' : 'No'}</p>

        <h5>Cost Breakdown</h5>
        <Table>
          <thead>
            <tr>
              <th>Description</th>
              <th>Contractor Amount</th>
              <th>Homeowner Amount</th>
            </tr>
          </thead>
          <tbody>
            {costBreakdown.map((item, index) => (
              <tr key={index}>
                <td>{item.description}</td>
                <td>${item.contractorAmount}</td>
                <td>${item.homeownerAmount}</td>
              </tr>
            ))}
          </tbody>
        </Table>

        <h5>Payment Schedule</h5>
        <Table>
          <thead>
            <tr>
              <th>Payment Condition</th>
              <th>Percentage</th>
            </tr>
          </thead>
          <tbody>
            {paymentSchedule.map((item, index) => (
              <tr key={index}>
                <td>{item.condition}</td>
                <td>{item.percentage}%</td>
              </tr>
            ))}
          </tbody>
        </Table>

        <h5>Attachments</h5>
        <ul>
          {files.map((file, index) => (
            <li key={index}>{file.name} - {(file.size / 1024 / 1024).toFixed(2)} MB</li>
          ))}
        </ul>
        <p>Total files selected: {files.length}</p>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>Edit</Button>
        <Button color="primary" onClick={onSubmit}>Submit Bid</Button>
      </ModalFooter>
    </Modal>
  );
};

export default BidPreviewModal;